import { Component } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Catches render-time errors in any child route so a single broken component
 * never blanks the whole studio. Error boundaries must be class components.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught an error:", error, info);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false });
    window.location.assign("/dashboard");
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5" data-testid="error-boundary">
          <div className="max-w-md w-full border border-white/10 rounded-md bg-[#121212] p-8 text-center">
            <div className="h-12 w-12 rounded-md bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="h-6 w-6 text-red-400" />
            </div>
            <h1 className="font-head text-2xl font-bold mb-2">Something went sideways</h1>
            <p className="text-sm text-zinc-400 mb-6">
              A part of the studio hit an unexpected error. Your work is safe — let's get you back.
            </p>
            <button
              onClick={this.handleReset}
              data-testid="error-reset-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
